import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CATEGORIES,
  deleteMember,
  fetchMembers,
  fetchPaymentsForPeriod,
  initials,
  periodOptions,
  savePayment,
} from "../../services/adminService";
import MemberFormModal from "./MemberFormModal";
import PaymentModal from "./PaymentModal";
import ImportMembersModal from "./ImportMembersModal";
import { useToast } from "./Toast";

const STATUS_LABEL = { paid: "Paid", unpaid: "Unpaid", unknown: "Unknown" };

function downloadCsv(rows, period) {
  const header = ["Member", "Membership number", "Status", "Email", "Phone", "Category", "Organization", "Approval", `Payment ${period}`, "Certificate"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [
    header.map(esc).join(","),
    ...rows.map((r) =>
      [r.fullName, r.memberNumber, r.status, r.email, r.phone, r.category, r.organization, r.approval, STATUS_LABEL[r.payStatus], r.certificate]
        .map(esc)
        .join(",")
    ),
  ];
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `nsk-members-${period}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function MembershipRegister() {
  const toast = useToast();
  const [period, setPeriod] = useState(String(new Date().getFullYear()));
  const [members, setMembers] = useState([]);
  const [payments, setPayments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [payFilter, setPayFilter] = useState("all");
  const [sortAsc, setSortAsc] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [memberModal, setMemberModal] = useState(null); // { member|null }
  const [paymentModal, setPaymentModal] = useState(null); // member
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [m, p] = await Promise.all([fetchMembers(), fetchPaymentsForPeriod(period)]);
      setMembers(m);
      setPayments(p);
    } catch (err) {
      console.error(err);
      setError("Could not load members. Check your connection and Firestore rules.");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(
    () =>
      members.map((m) => {
        const s = payments[m.id]?.status;
        return { ...m, payStatus: s === "paid" ? "paid" : s === "unpaid" ? "unpaid" : "unknown" };
      }),
    [members, payments]
  );

  const stats = useMemo(
    () => ({
      total: rows.length,
      paid: rows.filter((r) => r.payStatus === "paid").length,
      unpaid: rows.filter((r) => r.payStatus === "unpaid").length,
      unknown: rows.filter((r) => r.payStatus === "unknown").length,
    }),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => category === "all" || r.category === category)
      .filter((r) => payFilter === "all" || r.payStatus === payFilter)
      .filter(
        (r) =>
          !q ||
          [r.fullName, r.email, r.phone, r.organization, r.memberNumber].some((v) => (v || "").toLowerCase().includes(q))
      )
      .sort((a, b) => (sortAsc ? 1 : -1) * a.fullName.localeCompare(b.fullName));
  }, [rows, search, category, payFilter, sortAsc]);

  const filtersActive = search.trim() !== "" || category !== "all" || payFilter !== "all";
  const clearFilters = () => {
    setSearch("");
    setCategory("all");
    setPayFilter("all");
  };

  const pct = (n) => (stats.total ? Math.round((n / stats.total) * 100) : 0);
  const width = (n) => `${stats.total ? (n / stats.total) * 100 : 0}%`;

  const pickCard = (key) => setPayFilter((cur) => (key === "all" || cur === key ? "all" : key));

  const markPaid = async (m) => {
    setBusyId(m.id);
    const prev = payments[m.id] || {};
    const record = {
      status: "paid",
      amount: prev.amount || 0,
      method: prev.method || "",
      reference: prev.reference || "",
      paidOn: new Date().toISOString().slice(0, 10),
    };
    try {
      await savePayment(m, period, record);
      setPayments((p) => ({
        ...p,
        [m.id]: { ...prev, ...record, memberId: m.id, memberName: m.fullName, period },
      }));
      toast.show(`${m.fullName} marked paid for ${period}. Add the amount under Payment if needed.`);
    } catch (err) {
      console.error(err);
      toast.show("Could not record the payment", "error");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.fullName} and all of their payment records? This cannot be undone.`)) return;
    try {
      await deleteMember(m);
      toast.show(`${m.fullName} deleted`);
      load();
    } catch (err) {
      console.error(err);
      toast.show("Could not delete this member", "error");
    }
  };

  const saved = (message) => {
    setMemberModal(null);
    setPaymentModal(null);
    if (message) toast.show(message);
    load();
  };

  const cards = [
    { key: "all", label: "Registration records", value: stats.total, note: "All members on the register" },
    { key: "paid", label: "Paid members", value: stats.paid, note: `${period} membership period` },
    { key: "unpaid", label: "Unpaid members", value: stats.unpaid, note: "Explicitly recorded as not paid" },
    { key: "unknown", label: "Unknown status", value: stats.unknown, note: "No recorded payment status" },
  ];

  return (
    <>
      <header className="na-page-head">
        <div>
          <h1>Membership register</h1>
          <p className="na-muted">Member records, executive approval, and payments by membership period.</p>
        </div>
        <div className="na-head-actions">
          <button className="na-btn" onClick={load}>Refresh</button>
          <button className="na-btn" onClick={() => setImportOpen(true)}>Import Excel</button>
          <button className="na-btn" onClick={() => downloadCsv(filtered, period)}>Export CSV</button>
          <button className="na-btn na-btn-primary" onClick={() => setMemberModal({ member: null })}>+ Insert member</button>
        </div>
      </header>

      <div className="na-period-row">
        <label className="na-inline">
          <strong>Membership period</strong>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {periodOptions().map((y) => (
              <option key={y}>{y}</option>
            ))}
          </select>
        </label>
        <span className="na-muted">Tap a card to filter the list</span>
      </div>

      <section className="na-stats">
        {cards.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`na-card na-stat na-stat-${c.key} ${payFilter === c.key || (c.key === "all" && payFilter === "all") ? "is-active" : ""}`}
            onClick={() => pickCard(c.key)}
          >
            <span className="na-muted">{c.label}</span>
            <strong>{c.value}</strong>
            <small>{c.note}</small>
          </button>
        ))}
      </section>

      <section className="na-progress" aria-label={`${stats.paid} of ${stats.total} members paid for ${period}`}>
        <div className="na-progress-bar">
          <span className="na-seg na-seg-paid" style={{ width: width(stats.paid) }} />
          <span className="na-seg na-seg-unpaid" style={{ width: width(stats.unpaid) }} />
          <span className="na-seg na-seg-unknown" style={{ width: width(stats.unknown) }} />
        </div>
        <p>
          <strong>Paid {stats.paid} of {stats.total} ({pct(stats.paid)}%)</strong> for {period}
          <span className="na-legend"><i className="na-dot na-dot-paid" />Paid</span>
          <span className="na-legend"><i className="na-dot na-dot-unpaid" />Unpaid</span>
          <span className="na-legend"><i className="na-dot na-dot-unknown" />Unknown</span>
        </p>
      </section>

      <section className="na-card na-table-card">
        <div className="na-toolbar">
          <input
            className="na-search"
            placeholder="Search name, email, phone, organization…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="all">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <select value={payFilter} onChange={(e) => setPayFilter(e.target.value)}>
            <option value="all">All payment statuses</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
            <option value="unknown">Unknown</option>
          </select>
          <span className="na-muted">{filtered.length} records</span>
        </div>

        {error && <p className="na-error na-pad">{error}</p>}

        <div className="na-table-wrap">
          <table className="na-table">
            <thead>
              <tr>
                <th className="na-sortable" onClick={() => setSortAsc(!sortAsc)}>Member {sortAsc ? "↑" : "↓"}</th>
                <th>Category</th>
                <th>Organization</th>
                <th>Approval</th>
                <th>Payment {period}</th>
                <th>Certificate</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="na-empty">Loading members…</td></tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr>
                  <td colSpan="7" className="na-empty">
                    <p>No members on the register yet.</p>
                    <button className="na-btn na-btn-primary" onClick={() => setMemberModal({ member: null })}>
                      + Insert the first member
                    </button>
                  </td>
                </tr>
              )}
              {!loading && rows.length > 0 && filtered.length === 0 && (
                <tr>
                  <td colSpan="7" className="na-empty">
                    <p>No members match your filters.</p>
                    {filtersActive && (
                      <button className="na-btn" onClick={clearFilters}>Clear filters</button>
                    )}
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="na-member">
                        <span className="na-avatar">{initials(r.fullName)}</span>
                        <div>
                          <strong>{r.fullName}</strong>
                          <small>{r.memberNumber || "No member no."} · {r.status}</small>
                          <small>{r.email}</small>
                        </div>
                      </div>
                    </td>
                    <td>{r.category || "—"}</td>
                    <td>{r.organization || "—"}</td>
                    <td><span className={`na-approval na-${r.approval.toLowerCase().replace(/\s+/g, "-")}`}>{r.approval}</span></td>
                    <td><span className={`na-badge na-badge-${r.payStatus}`}>{STATUS_LABEL[r.payStatus]}</span></td>
                    <td>{r.certificate}</td>
                    <td className="na-actions">
                      {r.payStatus !== "paid" && (
                        <button className="na-quick" disabled={busyId === r.id} onClick={() => markPaid(r)}>
                          {busyId === r.id ? "Saving…" : "Mark paid"}
                        </button>
                      )}
                      <button onClick={() => setMemberModal({ member: r })}>Edit</button>
                      <button onClick={() => setPaymentModal(r)}>Payment</button>
                      <button className="na-danger" onClick={() => remove(r)}>Delete</button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      {importOpen && (
        <ImportMembersModal
          members={members}
          payments={payments}
          period={period}
          categories={CATEGORIES}
          onClose={() => setImportOpen(false)}
          onImported={(message) => {
            setImportOpen(false);
            toast.show(message);
            load();
          }}
        />
      )}
      {memberModal && (
        <MemberFormModal member={memberModal.member} onClose={() => setMemberModal(null)} onSaved={saved} />
      )}
      {paymentModal && (
        <PaymentModal
          member={paymentModal}
          period={period}
          payment={payments[paymentModal.id]}
          onClose={() => setPaymentModal(null)}
          onSaved={saved}
        />
      )}
    </>
  );
}
